import React, { useEffect, useState } from "react";
import { Form, Formik } from "formik";
import List, { STATE } from "../../../List/index";
import SecondaryPagination from "../../../SecondaryPagination/SecondaryPagination";
import { TextBox, Button } from "../../..";

import { ReactComponent as AddColorSvg } from "../../../../assets/admin/icons/add-color.svg";
import { ReactComponent as DeleteColorSvg } from "../../../../assets/admin/icons/delete-color.svg";
import { ReactComponent as DeleteDisableSvg } from "../../../../assets/admin/icons/delete-disable.svg";
import { ReactComponent as EyeSvg } from "../../../../assets/admin/icons/eye.svg";

import SearchBox from "../../SearchBox";
import useUsers from "../../../../hooks/useUsers";
import { format } from "date-fns";
import View from "./View";

const columns = [
  {
    display: "UID",
    key: "_id",
    textAlign: "left",
    maxWidth: 60,
  },
  {
    display: "Name",
    key: "name",
    textAlign: "left",
  },
  {
    display: "Email",
    key: "email",
    textAlign: "left",
  },
  {
    display: "Plan",
    key: "plan",
    textAlign: "left",
    maxWidth: 80,
  },
  {
    display: "Joined",
    key: "joined",
    textAlign: "left",
    maxWidth: 80,
  },
  {
    display: "",
    key: "status",
    textAlign: "left",
    maxWidth: 100,
  },
  {
    display: "",
    key: "actions",
    textAlign: "left",
    maxWidth: 32,
  },
];

const Customer = () => {
  const [dataState, setDataState] = useState(STATE.LOADING);
  const [customers, setCustomers] = useState(undefined);
  const [metadata, setMetadata] = useState(undefined);
  const [limit, setLimit] = useState(10);
  const [page, setPage] = useState(1);
  const [filter, setFilter] = useState({});
  const [summary, setSummary] = useState(undefined);

  // * Toggles
  const [toggleView, setToggleView] = useState(false);
  const [selected, setSelected] = useState(undefined);

  const { queryUsers, queryPlans, getUserSummary } = useUsers();

  useEffect(() => {
    fetchData();
    return () => {};
  }, []);

  useEffect(() => {
    fetchData();
  }, [filter, limit, page]);

  // * Fetch data
  const fetchData = async () => {
    const _filter = {
      $and: [{ deleted: false }, filter],
    };

    const { data, _metadata } = await queryUsers(_filter, page, limit);

    const { data: plans, _metadata: planMetadata } = await queryPlans();

    try {
      let _customers = data.map(
        ({
          _id,
          no,
          firstname,
          surname,
          email,
          plan,
          dateOfBirth,
          createdOn,
          active,
        }) => {
          const _joined = createdOn
            ? format(new Date(createdOn), "dd/MM/yyyy")
            : "";
          const _dateOfBirth = dateOfBirth
            ? format(new Date(dateOfBirth), "dd/MM/yyyy")
            : "";

          return {
            _id: (no || 1).toString().padStart(6, "0"),
            name: `${firstname} ${surname || ""}`,
            email,
            plan: plans.find((p) => p._id === plan)?.name,
            dateOfBirth: _dateOfBirth,
            joined: _joined,
            status: (
              <div className="admin-users-item-status">
                <label
                  className={`admin-users-item-status__label admin-users-item-status__label--${
                    active ? "active" : "inactive"
                  }`}
                >
                  {active ? "Active" : "Inactive"}
                </label>
              </div>
            ),
            actions: (
              <div className="admin-users-item-actions">
                <div
                  className="admin-users-item-actions-action"
                  onClick={() => handleView(_id)}
                >
                  <EyeSvg />
                </div>
              </div>
            ),
          };
        }
      );

      setCustomers(_customers);
      setMetadata(_metadata);
      setDataState(STATE.IDLE);

      // * Get summary
      const { data: summary } = await getUserSummary();
      setSummary(summary);
    } catch (error) {
      console.error(error);
    }
  };

  const handleSelected = (row) => {};

  const handleOnPageChange = (page) => setPage(page);

  const handleOnLimitChange = (limit) => {
    setPage(1);
    setLimit(limit);
  };

  const handleSearchChange = (value) => {
    if (!value) {
      setFilter({});
      return;
    }

    setPage(1);
    setFilter({
      $or: [
        {
          no: isNaN(value) ? 0 : +value,
        },
        {
          email: { $regex: value },
        },
      ],
    });
  };

  const handleAddUser = () => {
    // * handleAddUser
    setSelected(undefined);
    setToggleView(true);
  };

  const handleView = (id) => {
    setSelected(id);
    setToggleView(true);
  };

  const handleCloseView = () => {
    setToggleView(false);
    fetchData();
  };

  return (
    <>
      <div className="admin-users">
        {summary && (
          <div className="admin-users-summary">
            <span className="admin-users-summary__title">Summary</span>
            <div className="admin-users-summary__box">
              <div className="admin-users-summary__box__title">Daily</div>
              {summary.daily}
            </div>
            <div className="admin-users-summary__box">
              <div className="admin-users-summary__box__title">Weekly</div>
              {summary.weekly}
            </div>
            <div className="admin-users-summary__box">
              <div className="admin-users-summary__box__title">Monthly</div>
              {summary.monthly}
            </div>
            <div className="admin-users-summary__box">
              <div className="admin-users-summary__box__title">Yearly</div>
              {summary.yearly}
            </div>
          </div>
        )}
        <div className="admin-users-header">
          <SearchBox placeholder="Email or UID" onChange={handleSearchChange} />
        </div>
        <List
          contentState={dataState}
          columns={columns}
          dataSource={customers}
          viewable={false}
          onRecordClick={(e) => handleSelected(e)}
        >
          <SecondaryPagination
            limits={[10, 50]}
            defaultPage={metadata?.currentPage || 1}
            pages={metadata?.totalPages || 1}
            defaultLimit={metadata?.pageSize}
            onPageChange={handleOnPageChange}
            onLimitChange={handleOnLimitChange}
          />
        </List>
      </div>
      {toggleView && <View editing={selected} onClose={handleCloseView} />}
    </>
  );
};

Customer.propTypes = {};

export default Customer;
